import React from "react"

import { TResolvedImage } from "@/types/kontent.types"
import { sanitizeHtml } from "@/lib/sanitizeHtml"
import { getSyncDictionary } from "@/lib/sync-dictionary"
import {
  cn,
  ensureHttpUrl,
  formatEventDate,
  formatEventTime,
} from "@/lib/utils"
import { Link, Tag, Typography, type TagProps } from "@/components/ui/atoms"
import { Picture } from "@/components/ui/molecules"

type TCardNewsEventProps = {
  tagLabel: TagProps["label"]
  image: TResolvedImage
  title: string
  description: string
  siteUrl: string
  startDate: Date
  endDate?: Date
  showStartHour?: boolean
  location: string
  showEndHour?: boolean
  className?: string
  id?: string
}

const CardNewsEvent = ({
  title,
  description,
  siteUrl,
  showStartHour,
  showEndHour,
  location,
  startDate,
  endDate,
  image,
  className,
  tagLabel,
}: TCardNewsEventProps) => {
  const dictionary = getSyncDictionary().page.evenements

  const formattedWebsite = ensureHttpUrl(siteUrl)

  return (
    <article
      className={cn(
        "card-news-event flex h-full flex-col items-stretch lg:col-span-2",
        className
      )}
    >
      <Picture
        src={image.src + "?w=1600&h=1200&fm=webp&q=80"}
        alt={image.alt}
        variant={"default"}
        tagLabel={tagLabel}
        width={image.width}
        height={image.height}
        alignLeft={true}
        className="shrink-0"
        isRounded={true}
      />
      <div className="flex h-full flex-col justify-between pt-7">
        <div className="h-full">
          <div className="flex justify-between pb-5">
            <Typography
              as="h3"
              className="font-500 text-neutral-400"
              variant="headingM"
            >
              {formatEventDate(startDate)}
              {showStartHour ? (
                <span className="label-m text-neutral-400">
                  <span className="px-3">·</span>
                  {formatEventTime(startDate)}
                </span>
              ) : null}
              {endDate ? " - " + formatEventDate(endDate) : null}
              {endDate && showEndHour ? (
                <span className="label-m text-neutral-400">
                  <span className="px-3">·</span>
                  {formatEventTime(endDate)}
                </span>
              ) : null}
            </Typography>
            <div className="ml-[.5rem] lg:ml-0">
              <Tag variant="light" icon="Pin" label={location} />
            </div>
          </div>

          <header>
            <Typography as="h3" variant="headingS" className="pb-5">
              {title}
            </Typography>
          </header>
          <Typography
            as="section"
            variant="paragraphS"
            className="line-clamp-3 text-neutral-400"
          >
            <span
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(description) }}
            ></span>
          </Typography>
        </div>

        <footer className="flex flex-wrap items-baseline justify-between pt-5">
          <div className="flex items-baseline"></div>
          <div className="flex w-full justify-end text-right md:w-auto">
            {formattedWebsite ? (
              <Link
                chapters={[title, dictionary.site]}
                title={title}
                variant="ghost"
                iconRight="ExternalLinkLine"
                className="self-end p-0 font-500"
                href={formattedWebsite}
                target="_blank"
              >
                {dictionary.site}
              </Link>
            ) : null}
          </div>
        </footer>
      </div>
    </article>
  )
}

export { CardNewsEvent, type TCardNewsEventProps }
